import { useEffect, useRef, useState } from 'react';
import {
  startLessonNotesPdf,
  getLessonNotesPdfStatus,
} from '../../../../queries/lessons';

// Intervalo entre as consultas de status e tempo máximo de espera.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;

/*
 * Gera o PDF de anotações da videoaula assim que o componente é montado.
 *
 * Quando a lesson (`/lessons/:slug`) já traz o campo `notesPdfUri`, o PDF já
 * existe e usamos essa URL diretamente — sem chamar o endpoint `notes-pdf`.
 *
 * Caso `notesPdfUri` não venha, a geração é assíncrona: o endpoint apenas
 * dispara o processamento (respondendo na hora, sem segurar a conexão) e o
 * front consulta o status em intervalos até o PDF ficar pronto. Enquanto isso
 * o botão de "Anotações da aula" deve ficar em carregamento e desabilitado —
 * por isso expomos `isLoading` (verdadeiro durante todo o polling) e `isReady`
 * (verdadeiro apenas quando o PDF já está pronto para abertura).
 */
export function useLessonNotesPdf(
  id: string,
  enabled: boolean,
  notesPdfUri?: string | null
) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && !notesPdfUri);
  const [isReady, setIsReady] = useState(false);
  // Guarda o timer do polling em andamento para poder cancelá-lo no cleanup.
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // A lesson já possui o PDF de anotações: usa direto, sem gerar de novo.
    if (notesPdfUri) {
      setPdfUrl(notesPdfUri);
      setIsReady(true);
      setIsLoading(false);
      return;
    }

    if (!id || !enabled) {
      setIsLoading(false);
      return;
    }

    let active = true;
    const controller = new AbortController();
    const deadline = Date.now() + POLL_TIMEOUT_MS;

    setIsLoading(true);
    setIsReady(false);

    const finish = (url?: string | null) => {
      if (!active) return;
      if (url) {
        setPdfUrl(url);
        setIsReady(true);
      }
      setIsLoading(false);
    };

    // Consulta o status de tempos em tempos até `ready`, `failed` ou timeout.
    const poll = async () => {
      if (!active) return;

      if (Date.now() >= deadline) {
        // Estourou o tempo máximo: encerra o carregamento sem PDF pronto.
        finish();
        return;
      }

      try {
        const { status, url } = await getLessonNotesPdfStatus(
          id,
          controller.signal
        );
        if (!active) return;

        if (status === 'ready') {
          finish(url);
          return;
        }
        if (status === 'failed') {
          // Em caso de falha o botão permanece desabilitado (sem carregamento).
          finish();
          return;
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        // Erro transitório: mantém o polling até `ready`/`failed`/timeout.
        console.error(error);
      }

      if (active) {
        pollTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    (async () => {
      try {
        // Dispara a geração; o endpoint responde na hora (202 processing) ou
        // já entrega a URL (200 ready) quando o PDF existe.
        const { status, url } = await startLessonNotesPdf(
          id,
          controller.signal
        );
        if (!active) return;

        if (status === 'ready') {
          finish(url);
          return;
        }
        if (status === 'failed') {
          finish();
          return;
        }

        // `processing`: entra no loop de polling.
        poll();
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(error);
        finish();
      }
    })();

    return () => {
      active = false;
      // Cancela requisições em andamento e para o polling da aula anterior,
      // evitando chamadas com o ID antigo ao trocar de aula.
      controller.abort();
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [id, enabled, notesPdfUri]);

  const openNotesPdf = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank');
  };

  return { openNotesPdf, isLoading, isReady };
}
